import { useApi } from "./useApi";
import { useFlipperQuery } from "./useFlipperQuery";
import { useSubscriptionQuery } from "./useSubscriptionQuery";
import { useUser } from "./useUserQuery";
import { resolveUpgradeTarget } from "./resolveUpgradeTarget";

type SubscriptionSeatsResponse = {
  subscription_seats?: Array<{ user?: { id?: number } | null }>;
};

export type UpgradeTarget = {
  target: string | null;
  isReady: boolean;
};

// For regular subscriptions only. ApiSubscriptions (Connect) manage upgrade
// separately via useSubscriptionDetails/useAccessRightsQuery and must not
// consume this hook.
export const useUpgradeTarget = (): UpgradeTarget => {
  const api = useApi();
  const user = useUser();
  const { data: subscription } = useSubscriptionQuery();
  const { data: flipper } = useFlipperQuery();

  const isOwner = !!user && !!subscription && subscription.owner_id === user.id;
  const isReseller = isOwner && !!subscription?.reseller_label;
  const flagOn = !!flipper?.requestUpgradeViaSalesforce;

  const needsSeatsQuery = !!(
    subscription?.id &&
    user &&
    flagOn &&
    !isOwner &&
    !isReseller
  );

  const seatsQuery = api.get.useQuery<SubscriptionSeatsResponse>(
    `/subscriptions/${subscription?.id}/seats`,
    null,
    {
      enabled: needsSeatsQuery,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const isSeatUser =
    !!user &&
    seatsQuery.data?.subscription_seats?.some(
      (seat) => seat?.user?.id === user.id,
    ) === true;

  const isReady = !needsSeatsQuery || !seatsQuery.isFetching;

  const target = resolveUpgradeTarget({
    subscriptionId: subscription?.id,
    isOwner,
    isReseller,
    isSeatUser,
    flagOn,
  });

  return { target, isReady };
};
